import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Sparkles, Users, CalendarClock, Trophy, Target, BookOpen, MessageSquare, BarChart3, CheckCircle2 } from "lucide-react";

const SKILL_SCALE = [
  { level: 1, label: "Learning", desc: "Inconsistent contact, forgets footwork, struggles to keep rallies going." },
  { level: 2, label: "Developing", desc: "Makes the shot occasionally but location/pace is unpredictable." },
  { level: 3, label: "Reliable", desc: "Can repeat the shot in practice with decent consistency." },
  { level: 4, label: "Competent", desc: "Shot holds up in live play and under moderate pressure." },
  { level: 5, label: "Mastered", desc: "Executes intentionally — placement, spin, and pace on demand." },
];

const BANDS = ["0-2/10", "3-4/10", "5-6/10", "7-8/10", "9-10/10"];

const OBJECTIVE_CRITERIA = [
  {
    skill: "Forehand",
    drill: "Feed from mid-court; student hits cross-court to a 6-ft-wide target zone at baseline corner.",
    bands: BANDS,
  },
  {
    skill: "Backhand",
    drill: "Feed to backhand from mid-court; drive or slice into the deep corner — inside lines, waist-high or below.",
    bands: BANDS,
  },
  {
    skill: "Serve",
    drill: "Serve into the diagonal 3-ft-wide stripe along the service line (deep-middle target).",
    bands: BANDS,
  },
  {
    skill: "Dink",
    drill: "Cross-court dink rally: land 10 consecutive dinks inside the kitchen, below net-cord height.",
    bands: BANDS,
  },
  {
    skill: "Volley",
    drill: "Partner feeds body-height balls at NVZ line; student punches back to deep corner, paddle face controlled.",
    bands: BANDS,
  },
  {
    skill: "Strategy",
    drill: "Play 10 rally-starters; score 1 point each time the student hits the correct 3rd-shot choice (drop vs drive) based on the return depth.",
    bands: BANDS,
  },
];

const COACH_SECTIONS = [
  {
    icon: Users,
    title: "1. Build your roster",
    body: [
      "Hit **Students → Add student**. You enter name, email, skill level, and goals — we email them a temporary password.",
      "Students can also self-register and select you as their coach.",
      "The more context you give (goals + current skill), the better AI plans perform.",
    ],
  },
  {
    icon: CalendarClock,
    title: "2. Schedule lessons the right way",
    body: [
      "**Private lesson** — pick a specific student.",
      "**Clinic** — set a max capacity; students can self-book until it fills.",
      "**Available slot** — publish open times; the first student to tap it gets the slot.",
      "Every booking notifies both parties in-app and via email.",
    ],
  },
  {
    icon: Sparkles,
    title: "3. Draft lesson plans with AI",
    body: [
      "On any lesson, tap **AI draft**. Provide a short focus (e.g. *'3rd-shot drops'*).",
      "For clinics, AI will factor in the group size and suggest rotation drills.",
      "Always review and edit — you know your court and students best.",
    ],
  },
  {
    icon: Target,
    title: "4. Rate progress properly (this matters)",
    body: [
      "**Rate after every lesson, not just big moments.** Trend lines tell the real story.",
      "Use this 1-5 scale consistently per skill (see scale below).",
      "**Only rate what you observed that session.** Leave other skills at the previous value if not tested.",
      "Be honest — inflated ratings flatten the progress curve and make coaching harder.",
      "Log one concrete *area to improve* per session: something specific they can work on solo.",
      "Use **Milestones** for the highlight moments (first clean ATP, first tournament win, reached 4.0 DUPR) — these show up on the student's timeline.",
    ],
  },
  {
    icon: Sparkles,
    title: "5. Capture post-lesson insights",
    body: [
      "Type 3–5 bullet observations (what worked, what didn't, how they responded).",
      "Tap **AI insights** — you get a clean summary with next-lesson focus and an encouraging note the student can read.",
      "This is where most coaches save the biggest time.",
    ],
  },
  {
    icon: BarChart3,
    title: "6. Use the data",
    body: [
      "The **Skill Radar** on each student shows current strengths. Point it out to them — seeing it motivates.",
      "**Skill Trend** reveals plateaus. If a skill is flat 3 weeks → change the drill approach.",
      "Analytics on the coach side tracks retention, revenue, and top students so you can focus.",
    ],
  },
];

const STUDENT_SECTIONS = [
  {
    icon: BookOpen,
    title: "Before your first lesson",
    body: [
      "Set clear goals on your profile — specificity helps (e.g., *'more consistent drops'* beats *'get better'*).",
      "Arrive 10 min early to warm up: 3 min light cardio, 3 min shoulder mobility, 4 min dinking warm-up.",
    ],
  },
  {
    icon: CalendarClock,
    title: "Booking lessons",
    body: [
      "Head to **Book Lesson** to see every slot your coach has opened.",
      "Confirmed bookings land in your email and the in-app bell.",
      "Cancel at least 24 hours ahead when possible — respects the coach and frees slots for others.",
    ],
  },
  {
    icon: Target,
    title: "How to read your Skill Radar",
    body: [
      "1 = Learning · 2 = Developing · 3 = Reliable · 4 = Competent · 5 = Mastered.",
      "Pay attention to your **weakest two** skills — that's where the next breakthrough lives.",
      "A flat trend line over weeks? Ask your coach to vary the drill setup.",
    ],
  },
  {
    icon: Trophy,
    title: "Making the most of each lesson",
    body: [
      "Read your coach's **AI insights** between lessons and try the recommended homework once on your own.",
      "Come with one question per lesson — turns passive learners into rapid improvers.",
      "Celebrate milestones — they stack up faster than you think.",
    ],
  },
  {
    icon: MessageSquare,
    title: "Stay in the loop",
    body: [
      "Message your coach for quick questions or video clips (voice memos coming soon).",
      "You'll get in-app and email notifications for every new plan, insight, and booking.",
    ],
  },
];

export default function Help() {
  const { user } = useAuth();
  const [tab, setTab] = useState(user?.role === "student" ? "student" : "coach");

  const sections = tab === "coach" ? COACH_SECTIONS : STUDENT_SECTIONS;

  return (
    <div className="space-y-8" data-testid="help-page">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Playbook</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">Coach & Player Guide</h1>
        <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
          Getting the most from PBCOACH.APP takes a few minutes to learn. These are the habits that separate the
          top-performing coaches on the platform from everyone else.
        </p>
      </div>

      {/* Tabs */}
      <div className="inline-grid grid-cols-2 border border-white/10" data-testid="help-tabs">
        <button
          onClick={() => setTab("coach")}
          className={`px-8 py-3 text-xs uppercase tracking-widest font-bold transition-colors ${
            tab === "coach" ? "bg-[#38BDF8] text-black" : "text-white hover:bg-white/5"
          }`}
          data-testid="help-tab-coach"
        >
          For Coaches
        </button>
        <button
          onClick={() => setTab("student")}
          className={`px-8 py-3 text-xs uppercase tracking-widest font-bold transition-colors ${
            tab === "student" ? "bg-[#38BDF8] text-black" : "text-white hover:bg-white/5"
          }`}
          data-testid="help-tab-student"
        >
          For Players
        </button>
      </div>

      {/* Sections */}
      <div className="grid md:grid-cols-2 gap-4">
        {sections.map((s, i) => {
          const I = s.icon;
          return (
            <div key={i} className="card-surface p-6" data-testid={`help-section-${i}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 border border-[#38BDF8]/50 text-[#38BDF8] grid place-items-center">
                  <I size={16} aria-hidden="true" />
                </div>
                <h3 className="font-display text-xl font-black uppercase tracking-tight">{s.title}</h3>
              </div>
              <ul className="space-y-2 text-sm text-white/85 leading-relaxed">
                {s.body.map((b, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-[#38BDF8] mt-1 flex-shrink-0" aria-hidden="true" />
                    <span dangerouslySetInnerHTML={{ __html: b.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>') }} />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Scale reference (both roles benefit) */}
      <div className="card-surface p-8" data-testid="skill-scale-reference">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Reference</div>
        <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">The 1-5 skill scale</h3>
        <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
          Used when coaches log progress after each lesson (forehand, backhand, serve, dink, volley, strategy).
          Consistency matters more than the number itself.
        </p>
        <div className="mt-6 grid md:grid-cols-5 gap-3">
          {SKILL_SCALE.map((s) => (
            <div key={s.level} className="border border-white/10 p-4">
              <div className="flex items-end gap-2">
                <div className="font-display text-5xl font-black text-[#38BDF8] leading-none">{s.level}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold pb-1">{s.label}</div>
              </div>
              <div className="text-xs text-white/80 mt-3 leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Objective rating criteria */}
      <div className="card-surface p-8" data-testid="objective-rating-criteria">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Objective Criteria</div>
        <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">How to rate progress without bias</h3>
        <p className="text-sm text-[#A3A3A3] mt-3 max-w-3xl">
          Subjective ratings drift. Use these measurable drills — run each for 10 reps and count successful ones.
          The success rate maps to the 1–5 scale. Track the same drills every 4–6 weeks to spot real trends.
        </p>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {OBJECTIVE_CRITERIA.map((row, i) => (
            <div key={i} className="border border-white/10 p-5" data-testid={`criteria-${row.skill.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-display text-xl font-black uppercase tracking-tight">{row.skill}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold">10 reps</div>
              </div>
              <div className="text-xs text-white/80 mb-3"><strong>Drill:</strong> {row.drill}</div>
              <div className="grid grid-cols-5 gap-1">
                {[1, 2, 3, 4, 5].map((lvl, idx) => (
                  <div key={lvl} className="text-center py-2 border border-white/5" title={row.bands[idx]}>
                    <div className="font-display font-black text-[#38BDF8]">{lvl}</div>
                    <div className="text-[9px] uppercase tracking-widest text-[#A3A3A3]">{row.bands[idx]}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 border-l-2 border-[#38BDF8] pl-4 py-2 text-xs text-[#A3A3A3]">
          <strong className="text-white">Pro tip:</strong> Record success counts in the coach notes (e.g. <em>"dink
          cross-court: 7/10"</em>). Over time, AI insight summaries can pick up these numbers and surface trends
          without you doing extra work.
        </div>
      </div>
    </div>
  );
}
